import palette from 'theme/palette';
import { Logger } from 'utilities';

const chartColorKeys = Object.keys(palette.chartColors) as Array<keyof typeof palette.chartColors>;

const COLOR_CACHE_KEY = 'dashboard_color_cache';

interface ColorCache {
  [key: string]: string;
}

const getColorCache = (): ColorCache => {
  try {
    const cached = localStorage.getItem(COLOR_CACHE_KEY);
    return cached ? JSON.parse(cached) : {};
  } catch (error) {
    Logger.error('Error getting color cache', { error: error instanceof Error ? error.message : 'Unknown error' });
    return {};
  }
};

const saveColorCache = (cache: ColorCache): void => {
  try {
    localStorage.setItem(COLOR_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    Logger.error('Error saving color cache', { error: error instanceof Error ? error.message : 'Unknown error' });
  }
};

const getCachedRandomColor = (key: string): string => {
  const cache = getColorCache();
  
  if (cache[key]) {
    return cache[key];
  }
  
  const array = new Uint8Array(3);
  crypto.getRandomValues(array);
  const newColor = `#${Array.from(array)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')}`;
  
  cache[key] = newColor;
  saveColorCache(cache);
  
  return newColor;
};

export const randomHexColor = (): string => {
  const array = new Uint8Array(3);
  crypto.getRandomValues(array);
  return `#${Array.from(array)
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')}`;
};

export const getColorFromPalette = (index: number): string => {
  if (index >= chartColorKeys.length) {
    return getCachedRandomColor(`palette_overflow_${index}`);
  }
  const colorKey = chartColorKeys[index];
  return palette.chartColors[colorKey] || getCachedRandomColor(`palette_fallback_${index}`);
};

export const getCachedColor = (key: string): string => getCachedRandomColor(key);

export const clearColorCache = (): void => {
  try {
    localStorage.removeItem(COLOR_CACHE_KEY);
  } catch (error) {
    Logger.error('Error clearing color cache', { error: error instanceof Error ? error.message : 'Unknown error' });
  }
};
